import { motion } from "framer-motion";
import { Fuel, Truck, Home, Container } from "lucide-react";

const SERVICES = [
  {
    icon: Fuel,
    title: "Petroleum Products Supply",
    text: "We deal in all petroleum products — PMS, AGO (diesel), DPK (kerosene) and LPFO — sourced from trusted depots and supplied in any volume you require.",
  },
  {
    icon: Truck,
    title: "Logistics & Distribution",
    text: "Nationwide logistics and distribution of petroleum products across Nigeria, powered by a modern, GPS-tracked tanker fleet and seasoned drivers.",
  },
  {
    icon: Container,
    title: "Haulage for Oil Marketers",
    text: "As a major transporter for top oil marketers, we lift products from depots and deliver them safely and promptly to wherever our clients designate.",
  },
  {
    icon: Home,
    title: "Door-to-Door Diesel Supply",
    text: "Premium diesel delivered straight to your home, office, estate, factory or facility by our 20,000-litre peddler trucks — convenient, metered and always on schedule.",
    highlight: true,
  },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Services() {
  return (
    <section id="services" className="py-24 md:py-32 bg-[#0E1733] border-y border-slate-800" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-2xl"
        >
          <p className="text-amber-500 font-display font-semibold tracking-[0.35em] uppercase text-xs mb-4" data-testid="services-eyebrow">What We Do</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight" data-testid="services-heading">
            Core Operations &amp; Services
          </h2>
          <p className="mt-4 text-slate-400 text-base" data-testid="services-subtext">
            From depot to doorstep, Motaps Global covers the full chain of petroleum supply and movement across Nigeria.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6" data-testid="services-grid">
          {SERVICES.map(({ icon: Icon, title, text, highlight }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
              className={`group rounded-2xl p-8 md:p-10 border transition-all duration-300 hover:-translate-y-1.5 ${
                highlight
                  ? "bg-gradient-to-br from-amber-600/20 to-[#1C2541] border-amber-600/50 hover:border-amber-500"
                  : "bg-[#1C2541]/60 border-slate-700/50 hover:border-amber-600/50"
              }`}
              data-testid={`service-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-amber-600/15 flex items-center justify-center mb-6 group-hover:bg-amber-600/25 group-hover:scale-110 transition-all duration-300">
                <Icon size={28} className="text-amber-500" />
              </div>
              <h3 className="font-display font-bold text-xl md:text-2xl text-white">{title}</h3>
              <p className="mt-3 text-slate-400 leading-relaxed">{text}</p>
              {highlight && (
                <a href="#order" data-testid="service-order-link" className="inline-flex items-center gap-2 mt-5 text-amber-500 font-semibold text-sm hover:gap-3 transition-all duration-200">
                  Place a delivery order &rarr;
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
